import type { ReactNode } from 'react';
import type { ButtonVariant } from '../Button/Button.types';
import type { FeedbackType } from '../shared/feedbackHelpers';
import { ModalFooter, ModalHeader } from './shared/ModalParts';
import { ModalScene } from './shared/ModalScene';
import './Modal.css';

const DEFAULT_BODY_TEXT =
  'You are abut to add or edit the benchmark, this will cause a recalculation between the benchmark and the external model results. Are you sure you want to continue?';

/** Feedback modal with a colored icon (success, warning, error, info) and a body paragraph. */
export type ModalFeedbackProps = {
  /** Visual feedback variant shown in the header icon. */
  feedbackType?: FeedbackType;
  title?: string;
  content?: ReactNode;
  showClose?: boolean;
  showOverlay?: boolean;
  danger?: boolean;
  primaryLabel?: string;
  tertiaryLabel?: string;
  /** Button variant of the leading (tertiary-slot) footer button. */
  tertiaryVariant?: ButtonVariant;
  /** When true the primary footer button is disabled. */
  primaryDisabled?: boolean;
  /** Disable the primary footer button while a `ReadOnlyProvider` is read-only, with its reason as a tooltip. */
  primaryDisableWhenReadOnly?: boolean;
  className?: string;
  onCloseClick?: () => void;
  onPrimaryClick?: () => void;
  onTertiaryClick?: () => void;
};

export function ModalFeedback({
  feedbackType = 'info',
  title = 'Long title',
  content = DEFAULT_BODY_TEXT,
  showClose = true,
  showOverlay = true,
  danger = false,
  primaryLabel,
  tertiaryLabel,
  tertiaryVariant,
  primaryDisabled = false,
  primaryDisableWhenReadOnly = false,
  className = '',
  onCloseClick,
  onPrimaryClick,
  onTertiaryClick,
}: ModalFeedbackProps) {
  const dialog = (
    <div
      className={['spark-modal', 'spark-modal-text', 'spark-modal-feedback', className]
        .filter(Boolean)
        .join(' ')}
      role="dialog"
      aria-modal="true"
      aria-labelledby="spark-modal-title"
    >
      <ModalHeader
        title={title}
        showSubtitle={false}
        showClose={showClose}
        feedbackType={feedbackType}
        onCloseClick={onCloseClick}
      />
      <div className="spark-modal-body spark-modal-body-text">
        {typeof content === 'string' ? (
          <p className="spark-modal-text-content">{content}</p>
        ) : (
          <div className="spark-modal-text-content">{content}</div>
        )}
      </div>
      <ModalFooter
        danger={danger}
        primaryLabel={primaryLabel}
        tertiaryLabel={tertiaryLabel}
        tertiaryVariant={tertiaryVariant}
        primaryDisabled={primaryDisabled}
        primaryDisableWhenReadOnly={primaryDisableWhenReadOnly}
        onPrimaryClick={onPrimaryClick}
        onTertiaryClick={onTertiaryClick}
      />
    </div>
  );

  return (
    <ModalScene showOverlay={showOverlay} size="text">
      {dialog}
    </ModalScene>
  );
}
